import { useEffect, useRef, useState } from "react";
import { monthLabel } from "./ReconMonthDialog";

interface Props {
  id: string;
  value: string;                      // "YYYY-MM"
  onChange: (ym: string) => void;
  // A month the picker must not offer (e.g. the previous month on or after the current).
  disabled?: (ym: string) => boolean;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// A month picker: a button showing the month, opening a year-paged grid of months.
export default function MonthPicker({ id, value, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState(() => Number(value.slice(0, 4)) || new Date().getFullYear());
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);

  return (
    <div className="mp" ref={box}>
      <button id={id} type="button" className="mp-trigger" aria-haspopup="dialog" aria-expanded={open}
        onClick={() => {
          if (!open) setYear(Number(value.slice(0, 4)) || year);   // reopen on the chosen year
          setOpen(!open);
        }}>
        <span className="mp-icon" aria-hidden="true">▦</span>
        <span className="mp-value">{value ? monthLabel(value) : "Choose a month"}</span>
        <span className="mp-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="mp-pop" role="dialog" aria-label="Choose a month">
          <div className="mp-head">
            <button type="button" className="mp-nav" aria-label="Previous year" onClick={() => setYear(year - 1)}>‹</button>
            <span className="mp-year">{year}</span>
            <button type="button" className="mp-nav" aria-label="Next year" onClick={() => setYear(year + 1)}>›</button>
          </div>
          <div className="mp-grid">
            {MONTHS.map((m, i) => {
              const ym = `${year}-${String(i + 1).padStart(2, "0")}`;
              const off = disabled?.(ym) ?? false;
              return (
                <button key={ym} type="button" disabled={off} aria-pressed={ym === value}
                  className={`mp-month${ym === value ? " active" : ""}`}
                  onClick={() => { onChange(ym); setOpen(false); }}>
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
